import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AskAiProvider,
  ASK_AI_PROVIDER_LABELS,
  ASK_AI_PROVIDERS,
  AskAiProviderConfig,
  hasAskAiConfig,
  hasAskAiKeyFor,
  loadAskAiConfig,
  switchAskAiProvider,
} from '../../services/aiProviderConfig'
import {
  AskAiChatTurn,
  explainContentWithAi,
  explainQuestionWithAi,
} from '../../services/askAiApi'
import AskAiConfigModal from './AskAiConfigModal'
import AskAiMarkdown from './AskAiMarkdown'

type AskAiQuestionTarget = {
  mode?: 'question'
  resultId: number
  questionId: number
  paperId: number
}

type AskAiContentTarget = {
  mode: 'content'
  title: string
  contentHtml: string
  selectedText?: string
}

export type AskAiAnswerPanelProps = {
  open: boolean
  onClose: () => void
} & (AskAiQuestionTarget | AskAiContentTarget)

type UiMessage = AskAiChatTurn & { id: string }

export default function AskAiAnswerPanel(props: AskAiAnswerPanelProps) {
  const { open, onClose } = props
  const isContent = props.mode === 'content'
  const resultId = !isContent ? props.resultId : 0
  const questionId = !isContent ? props.questionId : 0
  const paperId = !isContent ? props.paperId : 0
  const contentTitle = isContent ? props.title : ''
  const contentHtml = isContent ? props.contentHtml : ''
  const selectedText = isContent ? props.selectedText || '' : ''
  const [configOpen, setConfigOpen] = useState(false)
  const [configProvider, setConfigProvider] = useState<AskAiProvider | null>(null)
  const [activeProvider, setActiveProvider] = useState<AskAiProvider>(
    () => loadAskAiConfig()?.provider || 'gemini'
  )
  const [collapsed, setCollapsed] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [entered, setEntered] = useState(false)
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState<UiMessage[]>([])
  const [draft, setDraft] = useState('')
  const [meta, setMeta] = useState<{ model?: string; imageCount?: number } | null>(null)
  const [imageDetail, setImageDetail] = useState<'low' | 'high'>('low')
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const sessionKeyRef = useRef('')

  const scrollToBottom = () => {
    window.setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
  }

  const openConfig = (provider?: AskAiProvider) => {
    setConfigProvider(provider || loadAskAiConfig()?.provider || 'gemini')
    setConfigOpen(true)
  }

  const callAi = async (opts?: {
    history?: AskAiChatTurn[]
    userMessage?: string
    reset?: boolean
  }) => {
    if (!hasAskAiConfig()) {
      openConfig('gemini')
      return
    }
    const cfg = loadAskAiConfig()
    if (!cfg?.apiKey) {
      openConfig('gemini')
      return
    }

    setActiveProvider(cfg.provider)
    setLoading(true)
    setError(null)
    try {
      const data = isContent
        ? await explainContentWithAi({
            provider: cfg.provider,
            apiKey: cfg.apiKey,
            model: cfg.model,
            title: contentTitle,
            contentHtml,
            selectedText: selectedText || undefined,
            chatHistory: opts?.history,
            userMessage: opts?.userMessage,
          })
        : await explainQuestionWithAi({
            provider: cfg.provider,
            apiKey: cfg.apiKey,
            model: cfg.model,
            resultId,
            questionId,
            paperId,
            imageDetail,
            chatHistory: opts?.history,
            userMessage: opts?.userMessage,
          })
      const assistantMsg: UiMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: data.markdown,
      }
      setMessages((prev) => (opts?.reset ? [assistantMsg] : [...prev, assistantMsg]))
      setMeta({ model: data.model, imageCount: data.imageCount })
      scrollToBottom()
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Ask AI thất bại'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const startFresh = async () => {
    setMessages([])
    setDraft('')
    await callAi({ reset: true })
  }

  const sendFollowUp = async () => {
    const text = draft.trim()
    if (!text || loading) return
    const history: AskAiChatTurn[] = messages.map(({ role, content }) => ({ role, content }))
    const userMsg: UiMessage = { id: `u-${Date.now()}`, role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setDraft('')
    scrollToBottom()
    await callAi({ history, userMessage: text })
  }

  const handleProviderPick = (provider: AskAiProvider) => {
    if (loading) return
    if (provider === activeProvider && hasAskAiKeyFor(provider)) return
    const switched = switchAskAiProvider(provider)
    if (switched) {
      setActiveProvider(provider)
      toast.info(`Đã chuyển sang ${ASK_AI_PROVIDER_LABELS[provider]}`)
      void startFresh()
      return
    }
    openConfig(provider)
  }

  useEffect(() => {
    if (!open) {
      setConfigOpen(false)
      setConfigProvider(null)
      setCollapsed(false)
      setExpanded(false)
      setEntered(false)
      return
    }

    setEntered(false)
    const raf = window.requestAnimationFrame(() => setEntered(true))

    const sessionKey = isContent
      ? `content:${contentTitle}:${selectedText.slice(0, 80)}:${contentHtml.length}`
      : `question:${resultId}:${questionId}:${paperId}`
    const isNewSession = sessionKeyRef.current !== sessionKey
    sessionKeyRef.current = sessionKey
    setCollapsed(false)

    if (!isNewSession && messages.length > 0) {
      return () => window.cancelAnimationFrame(raf)
    }

    setMessages([])
    setDraft('')
    setMeta(null)
    setError(null)
    setActiveProvider(loadAskAiConfig()?.provider || 'gemini')
    if (!hasAskAiConfig()) {
      openConfig('gemini')
      return () => window.cancelAnimationFrame(raf)
    }
    setConfigOpen(false)
    void startFresh()
    return () => window.cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resultId, questionId, paperId, isContent, contentTitle, contentHtml, selectedText])

  const handleConfigClose = () => {
    setConfigOpen(false)
    setConfigProvider(null)
    if (!hasAskAiConfig()) onClose()
  }

  const handleSaved = (cfg: AskAiProviderConfig) => {
    setConfigOpen(false)
    setConfigProvider(null)
    if (cfg.apiKey && cfg.apiKey.length >= 10) {
      setActiveProvider(cfg.provider)
      void startFresh()
    } else if (!hasAskAiConfig()) {
      onClose()
    }
  }

  if (!open) return null

  const panelSizeCls = collapsed
    ? 'bottom-4 right-4 h-12 w-[min(22rem,calc(100vw-2rem))] rounded-full'
    : expanded
      ? 'bottom-0 right-0 top-0 h-full w-full rounded-none sm:bottom-3 sm:right-3 sm:top-3 sm:h-auto sm:max-h-[calc(100vh-1.5rem)] sm:w-[min(56rem,calc(100vw-1.5rem))] sm:rounded-2xl'
      : 'bottom-0 right-0 h-[min(90vh,820px)] w-full rounded-t-2xl sm:bottom-4 sm:right-4 sm:w-[min(36rem,calc(100vw-2rem))] sm:rounded-2xl md:w-[min(40rem,calc(100vw-2rem))]'

  return (
    <>
      <div
        className={`fixed z-[70] flex flex-col overflow-hidden border border-m2-outline-variant bg-m2-surface-container shadow-2xl transition-all duration-300 ease-out ${panelSizeCls} ${
          entered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}
        role="dialog"
        aria-label="Ask AI"
        aria-expanded={!collapsed}
      >
        <div
          className={`flex shrink-0 items-center gap-2 border-b border-m2-outline-variant bg-m2-surface-low ${
            collapsed ? 'h-full border-b-0 px-3' : 'px-3 py-2.5'
          }`}
        >
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            title={collapsed ? 'Mở rộng Ask AI' : 'Thu gọn Ask AI'}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-m2-primary/15 text-m2-primary">
              <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-robot'} text-sm`} />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold text-m2-on-surface">Ask AI</span>
              {!collapsed && (
                <span className="block truncate text-[11px] text-m2-on-surface-variant">
                  {ASK_AI_PROVIDER_LABELS[activeProvider]}
                  {expanded ? ' · phóng to' : ''}
                </span>
              )}
              {collapsed && (
                <span className="block truncate text-[11px] text-m2-on-surface-variant">
                  {loading ? 'Đang trả lời…' : 'Bấm để mở lại'}
                </span>
              )}
            </span>
          </button>

          <div className="flex shrink-0 items-center gap-1">
            {!collapsed && (
              <>
                <button
                  type="button"
                  onClick={() => openConfig(activeProvider)}
                  className="rounded-lg px-2 py-1.5 text-xs font-semibold text-m2-on-surface-variant hover:bg-m2-surface-high"
                  title="Cấu hình API key"
                >
                  <i className="fas fa-cog" />
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="rounded-lg px-2 py-1.5 text-xs font-semibold text-m2-on-surface-variant hover:bg-m2-surface-high"
                  title={expanded ? 'Thu nhỏ panel' : 'Phóng to panel'}
                >
                  <i className={`fas ${expanded ? 'fa-compress' : 'fa-expand'}`} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExpanded(false)
                    setCollapsed(true)
                  }}
                  className="rounded-lg px-2 py-1.5 text-xs font-semibold text-m2-on-surface-variant hover:bg-m2-surface-high"
                  title="Thu gọn"
                >
                  <i className="fas fa-chevron-down" />
                </button>
              </>
            )}
            {collapsed && (
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                className="rounded-lg px-2 py-1.5 text-xs font-semibold text-m2-primary hover:bg-m2-primary/10"
                title="Mở lại"
              >
                <i className="fas fa-chevron-up" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2 py-1.5 text-xs font-semibold text-m2-on-surface-variant hover:bg-m2-surface-high"
              title="Đóng"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {!collapsed && (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-m2-outline-variant px-3 py-1.5 text-[11px]">
              <span className="font-semibold text-m2-on-surface-variant">Provider</span>
              <div className="flex flex-wrap gap-1">
                {ASK_AI_PROVIDERS.map((p) => {
                  const active = p === activeProvider
                  const hasKey = hasAskAiKeyFor(p)
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={loading}
                      onClick={() => handleProviderPick(p)}
                      title={
                        hasKey
                          ? `Dùng ${ASK_AI_PROVIDER_LABELS[p]}`
                          : `Cấu hình API key cho ${ASK_AI_PROVIDER_LABELS[p]}`
                      }
                      className={`rounded-md border px-2 py-0.5 font-semibold disabled:opacity-50 ${
                        active
                          ? 'border-m2-primary bg-m2-primary/15 text-m2-primary'
                          : 'border-m2-outline-variant text-m2-on-surface-variant hover:border-m2-primary/40'
                      }`}
                    >
                      {ASK_AI_PROVIDER_LABELS[p]}
                      {p === 'gemini' ? ' · Free' : ''}
                      {!hasKey ? ' · +' : ''}
                    </button>
                  )
                })}
              </div>
              {!isContent && (
                <label className="ml-auto inline-flex items-center gap-1.5 text-m2-on-surface-variant">
                  <input
                    type="checkbox"
                    checked={imageDetail === 'high'}
                    onChange={(e) => setImageDetail(e.target.checked ? 'high' : 'low')}
                  />
                  Ảnh chi tiết
                </label>
              )}
              <button
                type="button"
                disabled={loading}
                onClick={() => void startFresh()}
                className="rounded-md border border-m2-primary/30 bg-m2-primary/10 px-2 py-0.5 font-semibold text-m2-primary disabled:opacity-50"
              >
                {loading && messages.length === 0 ? 'Đang hỏi…' : 'Hỏi lại'}
              </button>
              {meta?.model && (
                <span className="w-full truncate text-m2-on-surface-variant sm:w-auto">
                  {meta.model}
                  {typeof meta.imageCount === 'number' ? ` · ${meta.imageCount} ảnh` : ''}
                </span>
              )}
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {error}
                </div>
              )}

              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[95%] rounded-2xl px-3 py-2 text-sm ${
                      m.role === 'user'
                        ? 'bg-m2-primary text-m2-on-primary'
                        : 'border border-m2-outline-variant bg-white text-[#222222]'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <AskAiMarkdown content={m.content} />
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-m2-primary">
                  <i className="fas fa-spinner fa-spin" />
                  AI đang trả lời…
                </div>
              )}

              {!loading && messages.length === 0 && !error && (
                <p className="text-sm text-m2-on-surface-variant">Đang chuẩn bị giải thích…</p>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-m2-outline-variant bg-m2-surface-low px-3 py-2.5">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={2}
                  value={draft}
                  disabled={loading}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      void sendFollowUp()
                    }
                  }}
                  placeholder="Hỏi thêm… (Enter gửi)"
                  className="min-h-[40px] max-h-28 flex-1 resize-y rounded-xl border border-m2-outline-variant bg-m2-surface-container px-3 py-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary disabled:opacity-50"
                />
                <button
                  type="button"
                  disabled={loading || !draft.trim()}
                  onClick={() => void sendFollowUp()}
                  className="shrink-0 rounded-xl bg-m2-primary px-3 py-2.5 text-sm font-semibold text-m2-on-primary disabled:opacity-50"
                >
                  Gửi
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <AskAiConfigModal
        open={configOpen}
        initialProvider={configProvider}
        onClose={handleConfigClose}
        onSaved={handleSaved}
      />
    </>
  )
}
